import axios from 'lib/axios'
import { queryClient } from 'lib/queryClient'
import { resetAccountState, store } from 'store/store'
import { setAccountSettings } from 'store/user/userSlice'
import { isStaleAccountSessionError, startNewAccountSession } from 'lib/accountSessionState'

/* A stale account-session cancellation comes back as a rejected promise. Code that does not catch
 * it would otherwise have the browser log it as an unhandled rejection, so quiet it in one place —
 * a stale cancellation is not a real failure. */
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleAccountSessionError(event.reason)) {
      event.preventDefault()
    }
  })
}

/* Browser-storage keys that belong to a single account. Switching accounts removes them all, so
 * the next account starts as clean as a fresh sign-in. Preferences not tied to an account (the
 * theme, 'onyxia-theme-mode') stay. Add any new per-account key to this list. */
export const ACCOUNT_SCOPED_STORAGE_KEYS = [
  'framework', // tenant cyber-framework
  'recently_added_vendors', // SSM vendor cache
  'vendor_added_at', // SSM vendor-added timestamps
  'cost_distribution', // SSM cost cache
  'devices-filter-model', // devices grid filter state
  'insightFilters', // insight filters
  'ssm_empty_banner_closed', // dismissed-banner flag
  'CROWDSTRIKE_FORM_VALUES', // in-progress integration form draft
  'SENTINEL_FORM_VALUES', // in-progress integration form draft
  'hiddenActivateCpiList', // activate-CPI list
  'USERS_SEARCH_TERMS' // user-management search history
] as const

type ResetHook = () => void

const resetHooks = new Set<ResetHook>()

/* Lets a cache that lives outside Redux hand over a function that clears it. Clearing an account
 * runs all of them, so this module needn't know what any cache holds and none gets left behind.
 * Returns a function that removes the one just added. */
export const registerAccountResetHook = (hook: ResetHook): (() => void) => {
  resetHooks.add(hook)
  return () => {
    resetHooks.delete(hook)
  }
}

/* Load what the current account needs at sign-in: fetch the tenant's settings and put them in the
 * store. Runs on every sign-in, and again after an account switch. */
export const bootstrapAccountSession = async (): Promise<void> => {
  try {
    const { data, status } = await axios.get('/api/v3/tenant')
    if (status === 200) {
      store.dispatch(setAccountSettings(data.data))
      localStorage.setItem('framework', JSON.stringify(data.data.cyFramework))
    }
  } catch (err) {
    // A newer account switch started after this fetch, marking it stale; drop it quietly so the
    // newer account's bootstrap is the one that wins.
    if (!isStaleAccountSessionError(err)) {
      console.error(err)
    }
  }
}

/* Make the app forget the current account: clear its Redux state, empty the React Query cache,
 * remove its browser-storage keys, and run every registered cache reset. */
export const teardownAccountSession = (): void => {
  // Start a new session first, so any request still running from the old account is dropped instead of applied.
  startNewAccountSession()
  store.dispatch(resetAccountState())
  queryClient.clear()
  ACCOUNT_SCOPED_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key))
  resetHooks.forEach((hook) => {
    try {
      hook()
    } catch (e) {
      console.error(e)
    }
  })
}

/* True only when the active account changes from one real account to a different one while signed
 * in. Signing in (none to an account) and signing out (an account to none) don't count. */
export const shouldReloadAccount = (previousTenantId?: string | null, nextTenantId?: string | null): boolean =>
  !!previousTenantId && !!nextTenantId && previousTenantId !== nextTenantId

/* Switch accounts start to finish: forget the old one, then load the new one. */
export const reloadAccountSession = async (): Promise<void> => {
  teardownAccountSession()
  await bootstrapAccountSession()
}

/* The one place that decides what a change of signed-in account means: signing in loads the account
 * once; switching clears the old one and loads the new; staying put or signing out does neither. App
 * hands the decision here so a single change can't kick off two loads at once. */
export const runAccountTransition = (previousTenantId?: string | null, nextTenantId?: string | null): Promise<void> => {
  if (shouldReloadAccount(previousTenantId, nextTenantId)) {
    return reloadAccountSession()
  }
  if (!previousTenantId && nextTenantId) {
    return bootstrapAccountSession()
  }
  return Promise.resolve()
}
